import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { getRequest } from '../../../../Helper/ApiClient';
import LoadingSpinner from '../../../../Abstracts/LoadingSpinner/LoadingSpinner';
import ExhibitorPopup from '../../Abstracts/ExhibitorPopup/ExhibitorPopup';
import type { IVtsExhibitor } from '../Admin/Exhibitors/Exhibitors';
import type { TAB_STATES } from '../Exhibition/ExhibitorBoothPopup/ExhibitorBoothPopup';
import { boothCssVars, boothPalette } from './brandPalette';
import { flushBoothEvents, trackBoothEvent } from './events';
import {
  boothPanelDomId,
  boothTabDomId,
  tabFromPopupParam,
  tabsForExhibitor,
} from './panels';
import type {
  BoothBadges,
  BoothPanelProps,
  BoothTabId,
  BoothTrack,
} from './panels';
import PromoBand from './PromoBand/PromoBand';
import Fascia from './Fascia/Fascia';
import ProgressLine from './ProgressLine/ProgressLine';
import MenuBand from './MenuBand/MenuBand';
import AssortmentPanel from './AssortmentPanel/AssortmentPanel';
import SuggestedPanel from './SuggestedPanel/SuggestedPanel';
import HighlightsPanel from './HighlightsPanel/HighlightsPanel';
import SamplesPanel from './SamplesPanel/SamplesPanel';
import MediaPanel from './MediaPanel/MediaPanel';
import FeedbackPanel from './FeedbackPanel/FeedbackPanel';
import ContactPanel from './ContactPanel/ContactPanel';
import styles from './BoothOverlay.module.scss?module';

const PANELS: Record<BoothTabId, ComponentType<BoothPanelProps>> = {
  assortment: AssortmentPanel,
  suggested: SuggestedPanel,
  highlights: HighlightsPanel,
  samples: SamplesPanel,
  media: MediaPanel,
  feedback: FeedbackPanel,
  contact: ContactPanel,
};

const TITLE_ID = 'booth-overlay-title';
/** Candidates for the focus trap; visibility and tabindex="-1" are checked separately. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');
/** Height of the sticky menu band, keep in sync with $menuBandHeight. */
const MENU_BAND_HEIGHT = 52;

interface BoothOverlayProps {
  boothNumber: number;
  onClose: () => void;
}

/**
 * Fullscreen brand booth of the Virtual Trade Show (P-4889): promo band, fascia, sticky menu
 * band and one panel per tab. The organiser booth (Naskor) keeps the old popup.
 */
export default function BoothOverlay({
  boothNumber,
  onClose,
}: BoothOverlayProps) {
  const exhibitorRequest = getRequest<IVtsExhibitor>(
    '/api/onlineshop/vts-booth-exhibitor/' + boothNumber,
    undefined,
    { retry: 1 }
  );
  const [searchParams] = useSearchParams();
  const exhibitor = exhibitorRequest.data || undefined;
  const isOrganiser = !!exhibitor?.organiser;
  const isError =
    exhibitorRequest.isError || (exhibitorRequest.isSuccess && !exhibitor);
  const phase = isOrganiser
    ? 'legacy'
    : exhibitor
    ? 'ready'
    : isError
    ? 'error'
    : 'loading';

  const [requestedTab] = useState(() =>
    tabFromPopupParam(searchParams.get('popup'))
  );
  const [selectedTab, setSelectedTab] = useState<BoothTabId | null>(null);
  const [badges, setBadges] = useState<BoothBadges>({});

  const overlayRef = useRef<HTMLDivElement>(null);
  const panelAreaRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openedFor = useRef<number | null>(null);

  const palette = useMemo(
    () => boothPalette(exhibitor?.ci_color),
    [exhibitor?.ci_color]
  );
  const tabs = useMemo(
    () => (exhibitor ? tabsForExhibitor(exhibitor, badges) : []),
    [exhibitor, badges]
  );
  const activeTab: BoothTabId =
    selectedTab && tabs.includes(selectedTab)
      ? selectedTab
      : tabs.includes(requestedTab)
      ? requestedTab
      : 'assortment';

  const exhibitorId = exhibitor?.id;
  const track = useCallback<BoothTrack>(
    (event, payload) => {
      if (exhibitorId !== undefined) {
        trackBoothEvent(event, exhibitorId, payload);
      }
    },
    [exhibitorId]
  );

  const onBadge = useCallback((tab: BoothTabId, count: number | null) => {
    setBadges((prev) =>
      prev[tab] === count ? prev : { ...prev, [tab]: count }
    );
  }, []);

  const handleClose = useCallback(() => {
    if (phase === 'ready') {
      track('overlay_closed');
    }
    flushBoothEvents();
    onClose();
  }, [phase, track, onClose]);

  // The overlay scrolls, not the page behind it. The old popup manages the body itself.
  useEffect(() => {
    if (isOrganiser) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOrganiser]);

  // Give focus back to the element that had it before the booth opened.
  useEffect(() => {
    const previous =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    return () => {
      previous?.focus({ preventScroll: true });
    };
  }, []);

  // The close button is the first stop: on open, and when loading turns into content or error.
  useEffect(() => {
    if (phase !== 'legacy') {
      closeButtonRef.current?.focus({ preventScroll: true });
    }
  }, [phase]);

  // Tab and Shift+Tab stay inside the booth. A dialog on top of it (focus outside the overlay,
  // for example the Listing Request modal) keeps its own focus handling.
  // Esc closes the booth, unless a dialog on top of it handles it. In a form field the first Esc
  // only leaves the field, so typed input is not lost; the second one closes.
  useEffect(() => {
    if (isOrganiser) {
      return;
    }
    const handleTab = (event: KeyboardEvent) => {
      const overlay = overlayRef.current;
      const current = document.activeElement;
      if (!overlay || !(current instanceof HTMLElement)) {
        return;
      }
      if (!overlay.contains(current)) {
        return;
      }
      const focusable = focusableIn(overlay);
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (current === first || current === overlay)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || current === overlay)) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      const overlay = overlayRef.current;
      const target = event.target;
      if (
        overlay &&
        target instanceof Node &&
        target !== document.body &&
        !overlay.contains(target)
      ) {
        return;
      }
      if (isFormField(target)) {
        // Let the browser clear a filled search field; otherwise just leave the field.
        if (
          target instanceof HTMLInputElement &&
          target.type === 'search' &&
          target.value !== ''
        ) {
          return;
        }
        event.preventDefault();
        target.blur();
        return;
      }
      event.preventDefault();
      handleClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === 'Tab') {
        handleTab(event);
      } else if (event.key === 'Escape') {
        handleEscape(event);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOrganiser, handleClose]);

  useEffect(() => {
    if (
      !exhibitor ||
      exhibitor.organiser ||
      openedFor.current === exhibitor.id
    ) {
      return;
    }
    openedFor.current = exhibitor.id;
    trackBoothEvent('overlay_opened', exhibitor.id, {
      tab: activeTab,
      package: exhibitor.package ?? null,
    });
  }, [exhibitor, activeTab]);

  const selectTab = (tab: BoothTabId) => {
    if (tab === activeTab) {
      return;
    }
    setSelectedTab(tab);
    track('tab_changed', { tab });

    // With the menu band stuck at the top, start the new panel right below it.
    const overlay = overlayRef.current;
    const panelArea = panelAreaRef.current;
    if (overlay && panelArea) {
      const offset =
        panelArea.getBoundingClientRect().top -
        overlay.getBoundingClientRect().top -
        MENU_BAND_HEIGHT;
      if (offset < 0) {
        overlay.scrollTop += offset;
      }
    }
  };

  if (isOrganiser && exhibitor) {
    const legacyTab = searchParams.get('popup')?.toUpperCase();
    const productSizeId = searchParams.get('productSize_id');

    return (
      <ExhibitorPopup
        exhibitor={exhibitor}
        isOpen={true}
        onClose={onClose}
        exhibitor_tab={legacyTab as TAB_STATES | undefined}
        scrollIntoViewProductSize_Id={
          productSizeId ? Number(productSizeId) : undefined
        }
      />
    );
  }

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={exhibitor ? TITLE_ID : undefined}
      aria-label={exhibitor ? undefined : 'Brand booth'}
      tabIndex={-1}
    >
      <div className={styles.sheet} style={boothCssVars(palette)}>
        {exhibitor ? (
          <>
            <PromoBand exhibitor={exhibitor} palette={palette} />
            <Fascia
              exhibitor={exhibitor}
              productsCount={badges.assortment ?? exhibitor.products_count}
              palette={palette}
              titleId={TITLE_ID}
              onClose={handleClose}
              closeButtonRef={closeButtonRef}
              track={track}
            />
            <ProgressLine exhibitor={exhibitor} />
            <MenuBand
              tabs={tabs}
              active={activeTab}
              badges={badges}
              onSelect={selectTab}
            />
            <div ref={panelAreaRef} className={styles.panelArea}>
              {tabs.map((tab) => {
                const Panel = PANELS[tab];

                return (
                  <div
                    key={tab}
                    role="tabpanel"
                    id={boothPanelDomId(tab)}
                    aria-labelledby={boothTabDomId(tab)}
                    hidden={tab !== activeTab}
                  >
                    <Panel
                      exhibitor={exhibitor}
                      tab={tab}
                      active={tab === activeTab}
                      palette={palette}
                      onBadge={onBadge}
                      track={track}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className={styles.sheetState}>
            <button
              ref={closeButtonRef}
              type="button"
              className={styles.close}
              aria-label="Close booth"
              onClick={handleClose}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            {isError ? (
              <p className={styles.sheetMessage}>
                This booth is not available.
              </p>
            ) : (
              <LoadingSpinner />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Visible elements of the overlay that Tab can reach, in DOM order. */
function focusableIn(overlay: HTMLElement): HTMLElement[] {
  return Array.from(
    overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      element.getAttribute('tabindex') !== '-1' &&
      element.offsetParent !== null &&
      !element.closest('[hidden]')
  );
}

/** Text inputs, selects and editable content, where Esc first leaves the field. */
function isFormField(target: EventTarget | null): target is HTMLElement {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}
